/**
 * @fileoverview SMS newsletter sign-up web component.
 *
 * Shopify has no native SMS-only marketing form, so this component posts the
 * phone number to the TJC middleware endpoint (`/Website/NewsletterSignUp`)
 * instead of the Shopify customer form. Configuration comes from data
 * attributes set in `sections/newsletter-subscription.liquid`.
 */

import { Component } from '@theme/component';

/**
 * @typedef {Object} NewsletterSignUpResponse
 * @property {{ status: number, message: string }} [response]
 */

/**
 * @typedef {Object} SmsNewsletterRefs
 * @property {HTMLFormElement} form - The SMS sign-up form
 * @property {HTMLInputElement} phoneInput - Phone number input
 * @property {HTMLInputElement} termsCheckbox - Terms agreement checkbox
 * @property {HTMLButtonElement} submitButton - Submit button
 * @property {HTMLElement} error - Error message container
 * @property {HTMLElement} success - Success message container
 */

/**
 * @extends {Component<SmsNewsletterRefs>}
 */
class NewsletterSmsComponent extends Component {
  requiredRefs = ['form', 'phoneInput', 'submitButton', 'error', 'success'];

  /** @type {AbortController | null} */
  #abortController = null;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * @returns {string}
   */
  get #apiUrl() {
    return this.dataset.apiUrl ?? '';
  }

  /**
   * @returns {Record<string, string>}
   */
  get #messages() {
    return {
      phoneRequired: this.dataset.errorPhoneRequired || 'Please enter your mobile number.',
      phoneInvalid: this.dataset.errorPhoneInvalid || 'Please enter a valid mobile number.',
      termsRequired: this.dataset.errorTermsRequired || 'Please agree to the Terms & Privacy.',
      generic: this.dataset.errorGeneric || 'Something went wrong. Please try again.',
      success: this.dataset.successMessage || 'Thank you for subscribing!',
    };
  }

  /**
   * Handles SMS form submission and posts to the newsletter API.
   *
   * @param {SubmitEvent} event
   */
  handleSubmit = async (event) => {
    event.preventDefault();

    const { phoneInput, termsCheckbox } = this.refs;
    const messages = this.#messages;
    const phone = phoneInput.value.trim();

    if (!phone) {
      this.#showError(messages.phoneRequired);
      phoneInput.focus();
      return;
    }

    if (!isValidPhone(phone)) {
      this.#showError(messages.phoneInvalid);
      phoneInput.focus();
      return;
    }

    if (termsCheckbox && !termsCheckbox.checked) {
      this.#showError(messages.termsRequired);
      termsCheckbox.focus();
      return;
    }

    if (!this.#apiUrl) {
      console.error('[SMS newsletter] Missing API URL configuration.');
      this.#showError(messages.generic);
      return;
    }

    this.#setLoading(true);

    try {
      await this.#subscribe(phone);
      this.#showSuccess(messages.success);
      this.refs.form.reset();
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error('[SMS newsletter] subscription failed', error);
      this.#showError(messages.generic);
    } finally {
      this.#setLoading(false);
    }
  };

  /**
   * Posts the phone number to the newsletter sign-up endpoint.
   *
   * @param {string} phone
   * @returns {Promise<NewsletterSignUpResponse>}
   */
  async #subscribe(phone) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const payload = {
      emailID: this.dataset.customerEmail || '',
      countryCode: '',
      isActive: true,
      phoneNumber: phone,
      comment: '',
      customerCode: this.dataset.customerCode || '',
      isGuest: this.dataset.isGuest !== 'false',
    };

    const response = await fetch(this.#apiUrl, {
      method: 'POST',
      headers: {
        accept: '*/*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      signal: this.#abortController.signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    /** @type {NewsletterSignUpResponse} */
    const data = await response.json();

    if (data?.response && data.response.status !== 0) {
      throw new Error(data.response.message || 'Subscription rejected');
    }

    return data;
  }

  /**
   * @param {boolean} loading
   */
  #setLoading(loading) {
    const { submitButton, error, success } = this.refs;

    submitButton.classList.toggle('is-loading', loading);
    submitButton.disabled = loading;

    if (loading) {
      error.textContent = '';
      error.style.display = 'none';
      success.textContent = '';
      success.style.display = 'none';
    }
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    const { error } = this.refs;
    error.textContent = message;
    error.style.display = 'block';
  }

  /**
   * @param {string} message
   */
  #showSuccess(message) {
    const { success } = this.refs;
    success.textContent = message;
    success.style.display = 'block';
  }
}

/**
 * Strips all non-digit characters from a phone number.
 *
 * @param {string} phone
 * @returns {string}
 */
const sanitizePhone = (phone) => phone.replace(/\D/g, '');

/**
 * Validates that a phone number contains at least 10 digits.
 *
 * @param {string} phone
 * @returns {boolean}
 */
const isValidPhone = (phone) => sanitizePhone(phone).length >= 10;

if (!customElements.get('newsletter-sms')) {
  customElements.define('newsletter-sms', NewsletterSmsComponent);
}
